import React, { useState, useEffect, useCallback } from "react";
import { Send, Lock } from "lucide-react";
import { useEntitySettings } from "../../../contexts/EntitySettingsContext";
import FormControl from "../../common/FormControl";
import Button from "../../common/Button";
import Skeleton from "../../common/Skeleton";
import NoDataFound from "../../common/NoDataFound";
import ApprovalDot, { type WorkflowState } from "../../common/ApprovalDot";
import Toast, { type ToastMessage } from "../../common/Toast";
// self-service: no RBAC gate — any logged-in user may apply for their own leave
import { useFieldValidation } from "../../../validation/useFieldValidation";
import { ValidatedInput } from "../../../validation/ValidatedFields";
import { Wallet } from "lucide-react";
import { listLeaveTypesDropdown, listLeaves, createLeave, submitLeave, myBalances } from "../../../services/hrService";
import { LeaveType, LeaveApplication, LeaveBalance } from "../../../services/hrTypes";

const today = () => new Date().toISOString().split("T")[0];

const ApplyLeave: React.FC = () => {
    const { formatDate } = useEntitySettings();
    const [types, setTypes] = useState<Pick<LeaveType, "id" | "name" | "code">[]>([]);
    const [leaves, setLeaves] = useState<LeaveApplication[]>([]);
    const [balances, setBalances] = useState<LeaveBalance[]>([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [leaveTypeId, setLeaveTypeId] = useState(0);
    const [fromDate, setFromDate] = useState(today());
    const [toDate, setToDate] = useState(today());
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const vh = useFieldValidation();

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [t, l, b] = await Promise.all([listLeaveTypesDropdown(), listLeaves(), myBalances()]);
            setTypes(t);
            setLeaves(l);
            setBalances(b);
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to load" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleApply = async () => {
        setFormError(null);
        const ok = vh.validateAll([
            { name: "leaveTypeId", code: 201, value: leaveTypeId ? String(leaveTypeId) : "", required: true },
            { name: "fromDate", code: 305, value: fromDate, required: true },
            { name: "toDate", code: 606, value: toDate, additional: { from_date: fromDate, to_date: toDate }, required: true },
            { name: "reason", code: 110, value: reason, required: false },
        ]);
        if (!ok) return;
        if (!leaveTypeId) { setFormError("Select a leave type."); return; }
        if (!fromDate || !toDate) { setFormError("Select from and to dates."); return; }
        if (toDate < fromDate) { setFormError("To date must be on or after from date."); return; }
        setSaving(true);
        try {
            const created = await createLeave({
                leave_type_id: leaveTypeId,
                from_date: fromDate,
                to_date: toDate,
                reason: reason || undefined,
            } as any);
            await submitLeave(created.id);
            setToast({ type: "success", message: "Leave application submitted" });
            setLeaveTypeId(0);
            setReason("");
            fetchAll();
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to apply" });
        } finally {
            setSaving(false);
        }
    };

    const typeName = (id: number) => types.find((t) => t.id === id)?.name || `Type #${id}`;

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <span style={{ fontWeight: 600, fontSize: "0.9375rem" }}>Apply for Leave</span>
            </div>

            <div className="netbody-content" style={{ overflowY: "auto" }}>
                {/* Available Balances strip */}
                {!loading && balances.length > 0 && (
                    <div data-testid="PNL_apply_leave_balances" style={{ marginBottom: "1rem" }}>
                        <div style={{ fontWeight: 600, fontSize: "0.875rem", marginBottom: 10 }}>Available Balances</div>
                        <div className="action-card-grid action-card-grid--compact">
                            {balances.map((b) => {
                                const available = b.allotted - b.used - b.pending;
                                return (
                                    <div key={b.id} className="action-card" data-testid={`BAL_apply_${b.leave_type_id}`}>
                                        <div className="action-card__header">
                                            <span className="action-card__icon"><Wallet size={18} /></span>
                                            <div className="action-card__title-block">
                                                <div className="action-card__title">{types.find((t) => t.id === b.leave_type_id)?.name ?? `Type #${b.leave_type_id}`}</div>
                                            </div>
                                            <span className="action-card__badge">{available} left</span>
                                        </div>
                                        <div className="action-card__meta action-card__meta--piped">
                                            <span className="action-card__meta-item">Allotted {b.allotted}</span>
                                            <span className="action-card__meta-item">Used {b.used}</span>
                                            <span className="action-card__meta-item">Pending {b.pending}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div style={{ padding: "1rem", border: "1px solid var(--border-subtle)", borderRadius: 10, marginBottom: "1rem" }}>
                    {formError && <div className="form-error" style={{ marginBottom: 8 }}>{formError}</div>}
                    <div className="flex items-end gap-3 flex-wrap">
                        <div style={{ minWidth: 200 }}>
                            <FormControl.SearchSelect
                                label="Leave Type"
                                required
                                value={leaveTypeId || null}
                                onChange={(v) => { setLeaveTypeId(v ? Number(v) : 0); vh.clearError("leaveTypeId"); }}
                                placeholder="Select leave type..."
                                options={types.map((t) => ({ value: t.id, label: `${t.name} (${t.code})` }))}
                                error={vh.errors.leaveTypeId}
                                data-testid="SEL_leave_type"
                            />
                        </div>
                        <div style={{ minWidth: 150 }}>
                            <ValidatedInput
                                vh={vh}
                                name="fromDate"
                                code={305}
                                required
                                label="From"
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                data-testid="INP_leave_from"
                            />
                        </div>
                        <div style={{ minWidth: 150 }}>
                            <ValidatedInput
                                vh={vh}
                                name="toDate"
                                code={606}
                                required
                                additional={{ from_date: fromDate, to_date: toDate }}
                                label="To"
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                data-testid="INP_leave_to"
                            />
                        </div>
                        <div style={{ flex: 1, minWidth: 200 }}>
                            <ValidatedInput
                                vh={vh}
                                name="reason"
                                code={110}
                                label="Reason"
                                type="text"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Optional"
                                data-testid="INP_leave_reason"
                            />
                        </div>
                        <Button
                            variant="fill"
                            icon={Send}
                            disabled={saving}
                            onClick={handleApply}
                            data-testid="BTN_leave_apply"
                        >
                            {saving ? "Submitting..." : "Apply"}
                        </Button>
                    </div>
                </div>

                <div className="table-list">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th table-list-cell--dot"></th>
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Leave Type</th>
                                <th className="table-list-th">From</th>
                                <th className="table-list-th">To</th>
                                <th className="table-list-th">Days</th>
                                <th className="table-list-th">Status</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={[{ width: "icon" }, "3xs", "md", "sm", "sm", "2xs", "pill"]} />}
                            {!loading && leaves.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="table-list-cell">
                                        <NoDataFound title="No applications" message="Apply for leave to see it here." />
                                    </td>
                                </tr>
                            )}
                            {!loading && leaves.map((l, idx) => (
                                <tr key={l.id} className="table-list-row" data-testid={`ROW_leave_app_${l.id}`}>
                                    <td className="table-list-cell table-list-cell--dot">
                                        <ApprovalDot state={(l.workflow_state || "draft") as WorkflowState} />
                                    </td>
                                    <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                                    <td className="table-list-cell"><span className="table-list-cell-title cell-name-lock">{l.workflow_state === 'approved' && (<Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />)}{typeName(l.leave_type_id)}</span></td>
                                    <td className="table-list-cell">{formatDate(l.from_date)}</td>
                                    <td className="table-list-cell">{formatDate(l.to_date)}</td>
                                    <td className="table-list-cell">{l.days}</td>
                                    <td className="table-list-cell">{l.workflow_state || "draft"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default ApplyLeave;
